var _a, _b, _c, _d, _e;
import { __decorate, __metadata, __param } from "tslib";
import _ from 'lodash';
import { inject, provide } from '@/inversify.config';
import Vue from 'vue';
import { DetailPluginLock } from '@/presentation/pages/detail/basic/interface_lock/DetailPluginLock';
import { getFolderListApi } from '@cores/apis/workspace_api/folder';
import { refreshWithParamsAsQuery } from '@/routers';
import formatRefiner from '@/infrastructure/utils/formatRefiner';
import { getViewDataImpl } from '@cores/apis/workspace_api/impl/viewData.impl';
import ptDialogAutoMounterManager from '@/services/PtDialogAutoMounterManager';
import { getShortQueryApi } from '@cores/apis/workspace_api/patents';
import { UserService } from '@/domains/user/services/user-service.interface';
import { SpaceService } from '@/domains/detail/space/types/space-service.interface';
import { FeatureConfig } from '@/domains/feature-config/types/feature-config.interface';
import { RouterService } from '@cores/services/vue-router-service';
import { UserCapacityService } from '@/domains/user/services/user-capacity-service.interface';
import { transferViewTypeForApi } from '@/domains/detail/dataset/view-type-transfer';
import { translateField } from '@/infrastructure/utils/fieldTranslate';
import { useSmartFilter } from '@/domains/hiro/composables/use-smart-filter';
const { find, get, set, keys, concat, assign, remove, cloneDeep, omit, uniq, invert, isEmpty, filter, forEach } = _;
const AdvancedSearchDialog = () => import('@/presentation/components/AdvancedSearch/AdvancedSearchDialog.vue');
// VIEW_DATA_CONFIG
// const VIEW_NAME_MAP = {
//     patentFolderPage: 'grid',
//     patentImagePage: 'imageText',
//     patentFullimgPage: 'fullimg',
//     patentThumbPage: 'thumb',
//     paperFolderPage: 'grid',
//     marketFolderPage: 'grid',
//     investmentFolderPage: 'grid',
//     mergerFolderPage: 'grid',
//     patentWordCloudPage: 'wordcloud',
//     patentLitePage: 'fasttablelist',
// } as const;
// const VIEW_NAME_MAP2 = {
//     grid: 'tablelist',
//     fasttablelist: 'fasttablelist',
//     imageText: 'standardlist',
//     fullimg: 'pdfimage',
//     thumb: 'thumbnail',
//     wordcloud: 'wordcloud',
// } as const;
const defaultFieldInfo = {
    folderId: null,
    conditions: {
        advanced: {
            logic: 'AND',
            name: '',
            uuid: '',
            value: '',
            outer_op: 'AND',
            inner_op: 'AND',
            inner_op_comment: 'AND',
        },
        general: {
            logical: 'AND',
            search: '',
        },
    },
    q: null,
    efq: null,
};
// export type IRouteName = keyof typeof VIEW_NAME_MAP;
const FIELD_POPER_WHITE_LIST = ['ADC', 'TTC', 'SEIC', 'IPC', 'IPC_M', 'LOC', 'UPC', 'CPC', 'CPC_ALL', 'CPC_M', 'FI', 'FTERM', 'GBC'];
export const DetailServiceSymbol = Symbol.for('DetailService');
let DetailService = class DetailService {
    constructor(userService, userCapacityService, spaceService, featureConfig, routerService) {
        this.userService = userService;
        this.userCapacityService = userCapacityService;
        this.spaceService = spaceService;
        this.featureConfig = featureConfig;
        this.routerService = routerService;
        this.onLoaded = _.noop;
        /** @deprecated 要逐步抛弃这个值, 改用patentsData, viewData */
        this.setting = {};
        this.folders = [];
        this.isFolderListLoading = false;
        // patents
        this.patentsData = {};
        this.isPatentsLoading = false;
        // 其他数据源数据接口返回;
        this.viewData = {};
        this.isViewLoading = false;
        // 兼容微前端数据源
        this.isAsideExpand = true;
        // toggleAsideExpand() {
        //     this.isAsideExpand = !this.isAsideExpand;
        // }
        // customFields
        this.customFields = [];
        /* Filter */
        this.filterInfo = cloneDeep(defaultFieldInfo);
    }
    get route() {
        return this.routerService.currentRoute;
    }
    updateSetting(setting) {
        this.setting = setting;
    }
    get FIELD_POPER_WHITE_LIST() {
        return FIELD_POPER_WHITE_LIST;
    }
    get spaceId() {
        return this.route.query.spaceId;
    }
    get folderId() {
        return this.route.query.folderId;
    }
    get sfq() {
        const { buildSfq } = useSmartFilter();
        // 为空必须要返回 '', 否则会导致无法清空 sfq
        return buildSfq() || '';
    }
    updateFolders(folders) {
        this.folders = uniq(concat(this.folders, folders));
    }
    updateFolder(folder) {
        const _folder = omit(folder, ['children']);
        const theFolder = find(this.folders, { folder_id: _folder.folder_id });
        if (theFolder) {
            assign(theFolder, _folder);
        }
        else {
            this.folders = concat(this.folders, _folder);
        }
    }
    addFolder(folder) {
        if (!folder)
            return;
        folder.patent_count = folder.patent_count || 0;
        this.folders = concat(this.folders, folder);
    }
    editFolder(folder) {
        if (!folder)
            return;
        const _folder = omit(folder, ['children']);
        const theFolder = find(this.folders, { folder_id: _folder.folder_id });
        if (!theFolder)
            return;
        _folder.patent_count = theFolder.patent_count;
        _folder.has_new_patents = get(theFolder, 'has_new_patents', false);
        forEach(_folder, (value, key) => {
            Vue.set(theFolder, key, value);
        });
    }
    deleteFolder(folder) {
        if (!folder)
            return;
        remove(this.folders, { folder_id: folder.folder_id });
        this.folders = cloneDeep(this.folders);
    }
    // 当前目录是否在目录中
    isInFolder(sub, parent) {
        let currentSearch = sub;
        // eslint-disable-next-line no-constant-condition
        while (true) {
            if (!currentSearch || currentSearch.parent_id === '-root' || currentSearch.parent_id === '') {
                break;
            }
            currentSearch = find(this.folders, { folder_id: currentSearch.parent_id });
            if (!currentSearch || currentSearch.folder_id === parent.folder_id) {
                return true;
            }
        }
        return false;
    }
    get qid() {
        return get(this.route, 'query.qid');
    }
    get efqid() {
        return get(this.route, 'query.efqid');
    }
    get cond() {
        return get(this.route, 'query.cond');
    }
    get refiner() {
        return formatRefiner({
            q: get(this.filterInfo, 'q', null),
            efq: get(this.filterInfo, 'efq', null),
        });
    }
    get isInDetailPage() {
        return this.route.path.includes('/detail');
    }
    // 获取当前视图类型
    get detailType() {
        return this.route.params.dataType || this.route.query.dataType;
    }
    get detailPlugin() {
        // @ts-ignore
        const plugin = DetailPluginLock.allRegisteredPlugins.get(this.detailType);
        if (!plugin) {
            console.error(`Detail Plugin unknown：${this.detailType}`);
        }
        return plugin;
    }
    get maxPerFolder() {
        return this.spaceService.getSpaceCapacityByKey(this.viewDataKeyConfig.maxPerFolder) || 2000;
    }
    /**
     * 兼容微前端数据源
     */
    get maxPerFolderOfSpace() {
        return this.spaceService.getSpaceCapacityByKey(this.viewDataKeyConfig.maxPerFolder) || 2000;
    }
    get maxOp() {
        return this.userCapacityService.getUserCapacityByKey(this.viewDataKeyConfig.maxOp) || 2000;
    }
    get viewDataType() {
        if (this.detailPlugin) {
            return this.detailPlugin.responseKeyNormalizerMapping.backendDataType;
        }
        console.error(`Unknown View Data Type：${this.detailType}`);
        return 'PATENT';
    }
    get viewDataKeyConfig() {
        return { ...this.detailPlugin?.responseKeyNormalizerMapping };
    }
    get isPatentsDetailType() {
        // @ts-ignore
        return this.detailType === 'patent';
    }
    // global
    get isDataLoading() {
        return this.isPatentsDetailType ? this.isPatentsLoading : this.isViewLoading;
    }
    get patentVisibleFields() {
        const visibleFields = get(this.patentsData, 'structure.fields.visible_fields', []);
        return translateField(visibleFields);
    }
    get fieldStructure() {
        return get(this.patentsData, 'structure.fields.fields_structure', {});
    }
    updateViewData(folderId, data) {
        if (folderId !== this.folderId)
            return;
        if (data && 'total' in data && 'page' in data) {
            this.viewData = data;
            const filter = get(data, 'q', {});
            this.updateFilterInfo({
                conditions: data.conditions || defaultFieldInfo.conditions,
                q: Object.keys(filter).length ? filter : null,
                efq: get(data, 'efq', ''),
            });
        }
        else {
            console.error('updateViewData invalid data structure: YOU MUST FIX THIS !!!', data);
        }
    }
    get viewDetails() {
        return get(this.viewData, 'details', []);
    }
    // 固定，被第三方使用
    get currentSort() {
        return get(this.route, 'query.sort');
    }
    // 固定，被第三方使用
    get currentPage() {
        return get(this.route, 'query.page');
    }
    get currentTab() {
        // 兼容数据源微前端
        return 'refine';
        // return get(this.route, 'query.tab') as string;
    }
    get rowColorsFieldInfo() {
        // @ts-ignore
        return get(this.patentsData, 'structure.colour_field_info', {});
    }
    get rowColors() {
        const colour_field_info = cloneDeep(this.rowColorsFieldInfo);
        if (isEmpty(colour_field_info))
            return [];
        const inverts = invert(colour_field_info.field_option_value);
        const colors = [];
        for (let i = 0; i < keys(colour_field_info.field_option_value).length; i++) {
            colors.push(inverts[i]);
        }
        return colors;
    }
    // 记默认路由视图
    // logViewName(name: IRouteName) {
    //     if (!name || !this.supportViewNames.includes(name)) return;
    //     this._detailViewTypeService.setDetailViewName(name);
    // }
    // get supportViewNames() {
    //     // 本方法和 VIEW_NAME_MAP 其实并没有直接关系，未来可删
    //     // let views = Object.keys(VIEW_NAME_MAP);
    //     // 从插件中获取所有叶子节点的Route Name
    //     const pluginRouteConfs = DetailPluginLock.getAllPluginsSorted()
    //         .map(plugin => plugin.getPluginSubRouteConfig());
    //     let pluginViewNames: IRouteName[] = [];
    //     forEach(pluginRouteConfs, (routeConf) => {
    //         pluginViewNames = [
    //             ...pluginViewNames,
    //             ...getLeafRoutes(routeConf).map(route => route.name as IRouteName)
    //         ].filter(name => name);
    //     });
    //     return uniq([
    //         // ...views,
    //         ...pluginViewNames
    //     ]);
    // }
    // getSupportViewConfig() {
    //     let views = _.filter(
    //         this.supportViewNames,
    //         i => !['patentFolderPage', 'patentImagePage', 'patentFullimgPage', 'patentThumbPage'].includes(i)
    //     );
    //     if (this.userService.userInfo.view_type_list?.length > 0) {
    //         views.unshift(...this.userService.userInfo.view_type_list);
    //     } else {
    //         // 如果init接口报错，没有默认视图，就只设置表格视图
    //         views = ['patentFolderPage']
    //     }
    //     return views;
    // }
    // getViewName(view: IRouteName) {
    //     // 获取支持的视图列表
    //     const views = this.getSupportViewConfig();
    //     // 如果不在支持的视图列表中，返回第一个视图
    //     if (!includes(views, view)) {
    //         const localViewName = this._detailViewTypeService.getDetailViewName();
    //         if (includes(views, localViewName)) {
    //             return localViewName;
    //         }
    //         return views[0] || 'patentFolderPage';
    //     }
    //     return view;
    // }
    // get defaultViewName() {
    //     // cn 默认表格视图 us/eu 默认图文视图 且无高级视图权限的情况下默认为全文附图
    //     let DefaultView = 'patentFolderPage';
    //     if (this.featureConfig.region !== 'CN') {
    //         if (this.userService.userInfo.view_type_list?.includes('patentImagePage')) {
    //             DefaultView = 'patentImagePage';
    //         } else {
    //             DefaultView = 'patentFullimgPage';
    //         }
    //     }
    //     const localViewName = this._detailViewTypeService.getDetailViewName();
    //     const isInFreeView = ['patentFolderPage', 'patentImagePage', 'patentFullimgPage', 'patentThumbPage'].includes(localViewName);
    //     if (this.userService.userInfo.is_free && !isInFreeView) {
    //         return DefaultView;
    //     }
    //     // return this.getViewName(
    //     //     localViewName as IRouteName ||
    //     //     DefaultView
    //     // );
    // }
    // get viewName() {
    //     return get(this.route, 'name');
    // }
    // get viewMode(): typeof VIEW_NAME_MAP[keyof typeof VIEW_NAME_MAP] {
    //     return 'grid' //get(VIEW_NAME_MAP, this.viewName);
    // }
    get viewMode2() {
        return transferViewTypeForApi(this.route.params.dataType, this.route.params.viewType);
    }
    setCustomFields(customFields) {
        this.customFields = customFields;
    }
    // 暂时兼容专利与其他数据的属性
    get custom_fields$() {
        if (this.isPatentsDetailType) {
            return get(this.patentsData, 'structure.fields.custom_fields', []);
        }
        return get(this.viewData, 'custom_fields', []);
    }
    get totalCount() {
        return this.isPatentsDetailType ? get(this.patentsData, 'structure.num', 0) : get(this.viewData, 'num', 0);
    }
    // 是否无数据
    get isNoData() {
        return this.totalCount <= 0;
    }
    // 是否无显示的自定义字段;
    get hasCustomField() {
        const customFieldsInSort = filter(this.custom_fields$, { is_hide: false });
        return customFieldsInSort.length > 0;
    }
    refetchFolders() {
        const detailService = this;
        detailService.folders = [];
        detailService.isFolderListLoading = true;
        return getFolderListApi({
            space_id: this.spaceId,
        })
            .then((folders) => {
            detailService.folders = folders;
            return folders;
        })
            .finally(() => {
            detailService.isFolderListLoading = false;
        });
    }
    // 给第三方数据源调用的专用，自己也可以用，但是不能加更多的sideEffects
    refreshFolderList() {
        return this.refetchFolders();
    }
    /**
     * 获取多数据源列表数据
     */
    getViewListData(page, sort, paramOverwrites = {}) {
        const data = {
            space_id: this.spaceId,
            folder_id: this.folderId,
            page: page,
            sort: sort,
            qid: this.qid,
            efqid: this.efqid,
            cond: this.cond,
            data_type: this.viewDataType,
            ...paramOverwrites,
        };
        this.isViewLoading = true;
        return getViewDataImpl(data).then(data => {
            this.setCustomFields(get(data, 'custom_fields', []));
            return data;
        }).catch(e => {
            this.viewData = {};
            throw e;
        }).finally(() => {
            this.isViewLoading = false;
        });
    }
    openAdvancedFilterDialog() {
        ptDialogAutoMounterManager.openAsPromise(AdvancedSearchDialog)
            .then((filterInfo) => this.doFiltering(filterInfo));
    }
    updateFilterInfo(filterInfo) {
        this.filterInfo = {
            ...this.filterInfo,
            ...filterInfo,
        };
    }
    doFiltering(filterInfo) {
        const fi = {
            q: filterInfo.q,
            efq: filterInfo.efq,
            conditions: filterInfo.conditions,
            folderId: this.folderId,
            sfq: filterInfo.sfq || this.sfq,
        };
        this.updateQuery(fi);
    }
    updateQuery(filterInfo) {
        const workspaceId = this.spaceId;
        const folderId = this.folderId || '';
        const { q, efq, conditions } = filterInfo;
        const outer_op = get(conditions, 'general.logical') || 'AND';
        if (!isEmpty(q)) {
            set(q, 'outer_op', outer_op);
        }
        let _q = q;
        if (!get(_q, '*') && !get(_q, 'pq') && !get(_q, 'wq')) {
            _q = null;
        }
        // 流程： 获取短query(当前) -> 路由跳转 -> 获取专利
        return getShortQueryApi({
            space_id: workspaceId,
            folder_id: folderId,
            wq: _q || {},
            wefq: efq,
            conditions: conditions || {},
        }).then((data) => {
            if (!this.isInDetailPage) {
                return;
            }
            return refreshWithParamsAsQuery({
                page: 1,
            }, {
                qid: data.qid,
                efqid: data.efqid,
                cond: data.condId,
                sfq: filterInfo.sfq,
                tab: 'refine',
            });
        });
    }
    unFilter() {
        this.filterInfo.q = null;
        this.filterInfo.efq = null;
        this.filterInfo.sfq = null;
        this.filterInfo.conditions = {
            advanced: {
                logic: 'AND',
                name: '',
                uuid: '',
                value: '',
                outer_op: 'AND',
                inner_op: 'AND',
                inner_op_comment: 'AND',
            },
            general: {
                logical: 'AND',
                search: '',
            },
        };
        this.filterInfo.folderId = this.folderId;
        this.updateQuery(this.filterInfo);
    }
};
DetailService = __decorate([
    provide(DetailServiceSymbol),
    __param(0, inject(UserService)),
    __param(1, inject(UserCapacityService)),
    __param(2, inject(SpaceService)),
    __param(3, inject(FeatureConfig)),
    __param(4, inject(RouterService)),
    __metadata("design:paramtypes", [typeof (_a = typeof UserService !== "undefined" && UserService) === "function" ? _a : Object, typeof (_b = typeof UserCapacityService !== "undefined" && UserCapacityService) === "function" ? _b : Object, typeof (_c = typeof SpaceService !== "undefined" && SpaceService) === "function" ? _c : Object, typeof (_d = typeof FeatureConfig !== "undefined" && FeatureConfig) === "function" ? _d : Object, typeof (_e = typeof RouterService !== "undefined" && RouterService) === "function" ? _e : Object])
], DetailService);
export { DetailService };
